/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckOutlined } from '@ant-design/icons';
import { ChevronDown, ChevronUp, X } from 'lucide-react';

const DROPDOWN_MAX_HEIGHT = 280;
const DROPDOWN_GAP = 8;

const normalizeValues = (value) => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (value == null || value === '') return [];
  return [String(value)];
};

const ModernMultiCheckboxSelect = ({
  value,
  options = [],
  onChange,
  placeholder = 'Select',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [panelPosition, setPanelPosition] = useState(null);
  const containerRef = useRef(null);
  const panelRef = useRef(null);

  const selected = useMemo(() => normalizeValues(value), [value]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const optionMap = useMemo(() => {
    const m = new Map();
    (options || []).forEach((o) => {
      const k = String(o.value ?? '').trim();
      if (k) m.set(k, o);
    });
    return m;
  }, [options]);

  const filteredOptions = useMemo(() => {
    const q = String(searchTerm || '').toLowerCase().trim();
    return (options || []).filter((o) => {
      if (!q) return true;
      const v = String(o.value ?? '').toLowerCase();
      const l = String(o.label ?? '').toLowerCase();
      return v.includes(q) || l.includes(q);
    });
  }, [options, searchTerm]);

  const updatePanelPosition = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP;
    const spaceAbove = rect.top - DROPDOWN_GAP;
    const openUpward = spaceBelow < 160 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(
      DROPDOWN_MAX_HEIGHT,
      Math.max(120, openUpward ? spaceAbove : spaceBelow)
    );

    setPanelPosition({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: 10050,
      maxHeight,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + DROPDOWN_GAP }
        : { top: rect.bottom + DROPDOWN_GAP }),
    });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setPanelPosition(null);
      return undefined;
    }
    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [isOpen, updatePanelPosition]);

  // Match typical "Select" UX: fresh search each open (important for modals that don't unmount).
  useEffect(() => {
    if (isOpen) setSearchTerm('');
  }, [isOpen]);

  const emitChange = (vals) => {
    if (typeof onChange === 'function') onChange(vals);
  };

  const toggleValue = (raw) => {
    const v = String(raw ?? '').trim();
    if (!v) return;
    if (selectedSet.has(v)) {
      emitChange(selected.filter((x) => x !== v));
    } else {
      emitChange([...selected, v]);
    }
  };

  const removeValue = (v) => emitChange(selected.filter((x) => x !== v));

  const dropdownPanel =
    isOpen && !disabled && panelPosition
      ? createPortal(
          <div
            ref={panelRef}
            className="modern-multi-checkbox-select__panel"
            onWheel={(e) => e.stopPropagation()}
            style={{
              ...panelPosition,
              backgroundColor: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              boxShadow:
                '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              boxSizing: 'border-box',
            }}
          >
            <input
              autoFocus
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                height: '36px',
                padding: '8px 10px',
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                fontSize: '14px',
                outline: 'none',
                color: '#1F2937',
                boxSizing: 'border-box',
                backgroundColor: '#fff',
                flexShrink: 0,
              }}
            />

            <div
              onWheel={(e) => e.stopPropagation()}
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                paddingRight: '4px',
                overscrollBehavior: 'contain',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => {
                  const optVal = String(opt.value ?? '').trim();
                  const checked = optVal !== '' && selectedSet.has(optVal);
                  return (
                    <div
                      key={optVal || String(opt.label)}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleValue(optVal);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '7px 10px',
                        cursor: 'pointer',
                        borderRadius: '8px',
                        marginBottom: '4px',
                        backgroundColor: checked ? '#F3F4F6' : 'transparent',
                      }}
                    >
                      <div
                        style={{
                          width: '18px',
                          height: '18px',
                          border: checked ? 'none' : '1px solid #D1D5DB',
                          borderRadius: '4px',
                          backgroundColor: checked ? '#3B82F6' : 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '10px',
                          flexShrink: 0,
                        }}
                      >
                        {checked && <CheckOutlined style={{ color: 'white', fontSize: '10px' }} />}
                      </div>
                      <span style={{ fontSize: '14px', color: '#374151', fontWeight: checked ? 500 : 400 }}>
                        {opt.label}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}>
                  No options found
                </div>
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', textAlign: 'left' }}>
      <div
        onClick={() => !disabled && setIsOpen((v) => !v)}
        style={{
          minHeight: '40px',
          padding: '4px 10px',
          borderRadius: '8px',
          border: isOpen ? '2px solid #3B82F6' : '1px solid #D1D5DB',
          backgroundColor: disabled ? '#F9FAFB' : '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s',
          boxShadow: isOpen ? '0 0 0 4px rgba(59, 130, 246, 0.1)' : 'none',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1, overflow: 'hidden' }}>
          {selected.length > 0 ? (
            selected.map((v) => {
              const label = optionMap.get(v)?.label || v;
              return (
                <span
                  key={v}
                  style={{
                    backgroundColor: '#EFF6FF',
                    color: '#1E40AF',
                    fontSize: '12px',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    border: '1px solid #DBEAFE',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {label}
                  {!disabled && (
                    <X
                      size={12}
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeValue(v);
                      }}
                    />
                  )}
                </span>
              );
            })
          ) : (
            <span style={{ color: '#9CA3AF', fontSize: '14px' }}>{placeholder}</span>
          )}
        </div>
        {isOpen ? <ChevronUp size={18} color="#3B82F6" /> : <ChevronDown size={18} color="#6B7280" />}
      </div>

      {dropdownPanel}
    </div>
  );
};

export default ModernMultiCheckboxSelect;
